import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { IHeaderPanelActionProps, HeaderPanelAction } from './HeaderPanelAction';

export interface IHeaderPanelProps {
  readonly title: string;
  readonly rootStyle?: StyleProps;
  readonly headerStyle?: StyleProps;
  readonly bodyStyle?: StyleProps;
  readonly titleStyle?: StyleProps;
  readonly actions?: IHeaderPanelActionProps[];

  readonly isCollapsible?: boolean;
  children?: React.ReactNode;
  readonly onCollapsingChanged?: () => void;
  readonly animatedCollapsing?: boolean;
  readonly isOpen?: boolean;
}

export interface StyleProps {
  inline?: object;
  className?: string;
}

const baseBlockName = 'dp-header-panel';

function getRootClasses(className?: string | null) {
  return classNames(baseBlockName, {
    [`${className}`]: !!className,
  });
}

function getHeaderClasses(className?: string) {
  return classNames(`${baseBlockName}__header`, {
    [`${className}`]: !!className,
  });
}

function getTitleClasses(className?: string) {
  return classNames(`${baseBlockName}__title`, {
    [`${className}`]: !!className,
  });
}

function getCollapseClasses(isOpen?: boolean) {
  return classNames(`${getRootClasses()}__collapse`, `flex-centering`, {
    [`rotate-180`]: isOpen,
  });
}

function getBodyClasses(className: string | undefined, isOpen: boolean) {
  return classNames(`${getRootClasses()}__body ${getRootClasses()}__dropdown`, {
    [`${getRootClasses()}__dropdown--open`]: isOpen,
    [`${className}`]: !!className,
  });
}

export function HeaderPanel({
  title,
  actions,
  rootStyle,
  headerStyle,
  bodyStyle,
  titleStyle,
  isCollapsible,
  children,
  onCollapsingChanged,
  animatedCollapsing,
  isOpen,
}: IHeaderPanelProps) {
  const [open, setOpen] = useState(!!isOpen);

  const onHeaderClick = useCallback(() => {
    setOpen(!open);

    onCollapsingChanged && onCollapsingChanged();
  }, [open, onCollapsingChanged]);

  const headerProps = isCollapsible ? { onClick: onHeaderClick } : {};
  const rootClass = rootStyle ? rootStyle.className : '';
  const headerClass = headerStyle ? headerStyle.className : '';
  const titleClass = titleStyle ? titleStyle.className : '';
  const bodyClass = bodyStyle ? bodyStyle.className : '';
  // TODO alex: when border collapsed it has double border on the bottom
  return (
    <div className={getRootClasses(rootClass)}>
      <header className={getHeaderClasses(headerClass)} {...headerProps}>
        <div className={getTitleClasses(titleClass)}>{title || ''}</div>
        <ul className={`${getRootClasses()}__actions`}>
          <li>
            <ul className={`${baseBlockName}__custom-actions`}>
              {actions && actions.map((action, index) => <HeaderPanelAction key={index} {...action} />)}
            </ul>
          </li>
          {isCollapsible && <HeaderPanelAction iconClassName={getCollapseClasses(open)} onClick={onHeaderClick} />}
        </ul>
      </header>
      <div className={getBodyClasses(bodyClass, open || !isCollapsible)}>{children}</div>
    </div>
  );
}
